// 用戶管理頁（含新增/編輯/刪除）- Material UI 版本
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  TableSortLabel,
  TablePagination,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon
} from '@mui/icons-material';

// 定義在元件外層，元件識別（type）才會固定，重新 render 不會讓輸入框整個被銷毀重建、失去焦點
function EditableCell({ user, field, label, type = 'text', editingUser, editFormData, setEditFormData, roles, isSuper }) {
  const isEditing = editingUser === user.id;

  if (!isEditing) {
    if (field === 'pictureUrl') {
      return user.pictureUrl ? (
        <Avatar src={user.pictureUrl} sx={{ width: 32, height: 32 }} />
      ) : null;
    }
    if (field === 'role.name') {
      return user.role?.name || '';
    }
    return user[field] || '';
  }

  if (type === 'select' && field === 'roleId') {
    return (
      <FormControl size="small" fullWidth disabled={!isSuper}>
        <Select
          value={editFormData.roleId || ''}
          onChange={(e) => setEditFormData({...editFormData, roleId: e.target.value})}
        >
          {roles.map(role => (
            <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      size="small"
      fullWidth
      value={editFormData[field] || ''}
      onChange={(e) => setEditFormData({...editFormData, [field]: e.target.value})}
      variant="outlined"
    />
  );
}

export default function UserAdminPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [roles, setRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null });
  const [syncConfirmDialog, setSyncConfirmDialog] = useState({ open: false, userId: null, name: '', department: '', position: '', mobile: '', currentDepartment: '', currentMobile: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // 只有最高權限可刪除及修改角色
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuper = currentUser.role?.name === '最高權限';

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        fetch('/api/users'),
        fetch('/api/roles')
      ]);
      
      const users = await usersRes.json();
      const rolesData = await rolesRes.json();
      
      const mappedUsers = users.map(u => ({ ...u, key: u.id, roleId: u.role?.id }));
      setOriginalData(mappedUsers);
      setData(mappedUsers);
      setRoles(rolesData);
    } catch (error) {
      setSnackbar({ open: true, message: '載入資料失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 即時搜尋過濾功能
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setPage(0);

    if (!value.trim()) {
      setData(originalData);
      return;
    }
    
    const searchTerm = value.toLowerCase();
    const filteredData = originalData.filter(user => {
      const displayName = user.displayName || '';
      const realName = user.realName || '';
      const mobile = user.mobile || '';
      const community = user.community || '';
      const department = user.department || '';
      const position = user.position || '';
      const email = user.email || '';
      
      const searchableText = `${displayName} ${realName} ${mobile} ${community} ${department} ${position} ${email}`.toLowerCase();
      
      return searchableText.includes(searchTerm);
    });
    
    setData(filteredData);
  };

  // 清除搜尋
  const handleClearSearch = () => {
    setSearchQuery('');
    setData(originalData);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 排序功能
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
    
    const sortedData = [...data].sort((a, b) => {
      let aValue = key.includes('.') ? key.split('.').reduce((obj, k) => obj?.[k], a) : a[key];
      let bValue = key.includes('.') ? key.split('.').reduce((obj, k) => obj?.[k], b) : b[key];
      
      aValue = aValue || '';
      bValue = bValue || '';
      
      if (key === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return direction === 'asc' 
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });
    
    setData(sortedData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // 編輯處理函數
  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditFormData({
      displayName: user.displayName || '',
      realName: user.realName || '',
      mobile: user.mobile || '',
      community: user.community || '',
      department: user.department || '',
      position: user.position || '',
      email: user.email || '',
      roleId: user.role?.id || ''
    });
  };

  const handleEditCancel = () => {
    setEditingUser(null);
    setEditFormData({});
  };

  const handleEditSave = async (userId) => {
    try {
      const response = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editFormData)
      });

      if (!response.ok) {
        throw new Error('更新失敗');
      }

      setSnackbar({ open: true, message: '更新成功', severity: 'success' });
      setEditingUser(null);
      setEditFormData({});
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: '保存失敗: ' + error.message, severity: 'error' });
    }
  };

  const handleDelete = async (userId) => {
    try {
      const response = await fetch(`/api/users/${userId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('刪除失敗');
      
      setSnackbar({ open: true, message: '刪除成功', severity: 'success' });
      setDeleteDialog({ open: false, userId: null });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
    }
  };

  const handleRefresh = async (userId) => {
    try {
      const response = await fetch(`/api/users/${userId}/refresh-line-profile`, { method: 'POST' });
      if (!response.ok) throw new Error('更新失敗');
      
      setSnackbar({ open: true, message: 'LINE 資料已更新', severity: 'success' });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: '更新失敗', severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 同步 OneIMC 社職：姓名+社別精準比對會直接套用；只有姓名比對到、社別不同時，跳出確認視窗讓管理員自己判斷
  const handleSyncOneimc = async (user) => {
    try {
      const response = await fetch(`/api/users/${user.id}/sync-oneimc`, { method: 'POST' });
      if (!response.ok) throw new Error('同步失敗');
      const result = await response.json();

      if (result.status === 'updated') {
        setSnackbar({ open: true, message: `已同步：社職「${result.position}」、手機「${result.mobile || '(未提供)'}」`, severity: 'success' });
        fetchData();
      } else if (result.status === 'unchanged') {
        setSnackbar({ open: true, message: `資料已經是最新：社職「${result.position}」、手機「${result.mobile || '(未提供)'}」`, severity: 'info' });
      } else if (result.status === 'confirm_needed') {
        setSyncConfirmDialog({
          open: true,
          userId: user.id,
          name: user.realName || user.name,
          department: result.department,
          position: result.position,
          mobile: result.mobile,
          currentDepartment: result.currentDepartment || '(未設定)',
          currentMobile: result.currentMobile || '(未設定)',
        });
      } else {
        setSnackbar({ open: true, message: 'OneIMC 查無此人資料', severity: 'warning' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: '同步失敗', severity: 'error' });
    }
  };

  const handleConfirmSyncOneimc = async () => {
    try {
      const response = await fetch(`/api/users/${syncConfirmDialog.userId}/sync-oneimc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmDepartment: syncConfirmDialog.department,
          confirmPosition: syncConfirmDialog.position,
          confirmMobile: syncConfirmDialog.mobile,
        }),
      });
      if (!response.ok) throw new Error('同步失敗');
      setSnackbar({ open: true, message: `已套用：社別「${syncConfirmDialog.department}」、社職「${syncConfirmDialog.position}」、手機「${syncConfirmDialog.mobile || '(未提供)'}」`, severity: 'success' });
      setSyncConfirmDialog({ open: false, userId: null, name: '', department: '', position: '', mobile: '', currentDepartment: '', currentMobile: '' });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: '同步失敗', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
        用戶管理
      </Typography>
      
      {/* 搜尋區域 */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={9} md={10}>
            <TextField
              fullWidth
              label="搜尋用戶資料"
              placeholder="搜尋LINE顯示名稱、真實姓名、手機、社團、社別、社職、Email..."
              value={searchQuery}
              onChange={handleSearchChange}
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearSearch}
              size={isMobile ? 'small' : 'medium'}
            >
              清除
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 手機版：卡片式清單，取代擠不下的多欄表格 */}
      {isMobile && (
        <Stack spacing={1.5}>
          {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => {
            const isEditing = editingUser === user.id;
            return (
              <Card key={user.id} variant="outlined">
                <CardContent sx={{ pb: '16px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    {user.pictureUrl ? (
                      <Avatar src={user.pictureUrl} sx={{ width: 44, height: 44 }} />
                    ) : (
                      <Avatar sx={{ width: 44, height: 44 }}>{(user.displayName || user.realName || '?').charAt(0)}</Avatar>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {isEditing ? (
                        <TextField
                          fullWidth size="small" label="LINE 顯示名稱"
                          value={editFormData.displayName || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, displayName: e.target.value })}
                        />
                      ) : (
                        <Typography sx={{ fontWeight: 600 }} noWrap>{user.displayName || '(無顯示名稱)'}</Typography>
                      )}
                      {!isEditing && (
                        <Chip
                          label={user.role?.name || ''}
                          size="small"
                          sx={{ mt: 0.5 }}
                          color={user.role?.name === '最高權限' ? 'error' : user.role?.name === '管理者' ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>

                  {isEditing ? (
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                      <TextField
                        fullWidth size="small" label="真實姓名"
                        value={editFormData.realName || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, realName: e.target.value })}
                      />
                      <TextField
                        fullWidth size="small" label="手機"
                        value={editFormData.mobile || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, mobile: e.target.value })}
                      />
                      <TextField
                        fullWidth size="small" label="社團"
                        value={editFormData.community || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, community: e.target.value })}
                      />
                      <TextField
                        fullWidth size="small" label="社別"
                        value={editFormData.department || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                      />
                      <TextField
                        fullWidth size="small" label="社職"
                        value={editFormData.position || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, position: e.target.value })}
                      />
                      <TextField
                        fullWidth size="small" label="Email"
                        value={editFormData.email || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      />
                      {isSuper && (
                        <FormControl fullWidth size="small">
                          <InputLabel>角色</InputLabel>
                          <Select
                            value={editFormData.roleId || ''}
                            label="角色"
                            onChange={(e) => setEditFormData({ ...editFormData, roleId: e.target.value })}
                          >
                            {roles.map(role => (
                              <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Stack>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">姓名：{user.realName || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">手機：{user.mobile || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">社團：{user.community || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">社別：{user.department || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">社職：{user.position || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>Email：{user.email || '—'}</Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    {isEditing ? (
                      <>
                        <Tooltip title="保存">
                          <IconButton size="small" color="primary" onClick={() => handleEditSave(user.id)}>
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="取消">
                          <IconButton size="small" onClick={handleEditCancel}>
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="編輯">
                          <IconButton size="small" color="primary" disabled={editingUser !== null} onClick={() => handleEdit(user)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {isSuper && (
                          <Tooltip title="刪除">
                            <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, userId: user.id })}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.lineId && !user.pictureUrl && (
                          <Tooltip title="刷新LINE資料">
                            <IconButton size="small" onClick={() => handleRefresh(user.id)}>
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.community === 'IMC' && (
                          <Tooltip title="同步 OneIMC 社職">
                            <IconButton size="small" color="secondary" onClick={() => handleSyncOneimc(user)}>
                              <SyncIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
          {data.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {searchQuery ? '未找到符合條件的用戶' : '暫無用戶資料'}
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      {/* 桌面版：多欄表格 */}
      {!isMobile && (
      <TableContainer component={Paper} elevation={1}>
        <Table size={isMobile ? 'small' : 'medium'} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'displayName'}
                  direction={sortConfig.key === 'displayName' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('displayName')}
                >
                  LINE 顯示名稱
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">LINE 頭像</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'realName'}
                  direction={sortConfig.key === 'realName' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('realName')}
                >
                  真實姓名
                </TableSortLabel>
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'mobile'}
                      direction={sortConfig.key === 'mobile' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('mobile')}
                    >
                      手機
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'community'}
                      direction={sortConfig.key === 'community' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('community')}
                    >
                      社團
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'department'}
                      direction={sortConfig.key === 'department' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('department')}
                    >
                      社別
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'position'}
                      direction={sortConfig.key === 'position' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('position')}
                    >
                      社職
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'email'}
                      direction={sortConfig.key === 'email' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('email')}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                </>
              )}
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'role.name'}
                  direction={sortConfig.key === 'role.name' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('role.name')}
                >
                  角色
                </TableSortLabel>
              </TableCell>
              {!isMobile && (
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'createdAt'}
                    direction={sortConfig.key === 'createdAt' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('createdAt')}
                  >
                    建立時間
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => {
              const isEditing = editingUser === user.id;
              
              return (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <EditableCell user={user} field="displayName" label="LINE 顯示名稱" editingUser={editingUser} editFormData={editFormData} setEditFormData={setEditFormData} roles={roles} isSuper={isSuper} />
                  </TableCell>
                  <TableCell align="center">
                    <EditableCell user={user} field="pictureUrl" editingUser={editingUser} editFormData={editFormData} setEditFormData={setEditFormData} roles={roles} isSuper={isSuper} />
                  </TableCell>
                  <TableCell>
                    <EditableCell user={user} field="realName" label="真實姓名" editingUser={editingUser} editFormData={editFormData} setEditFormData={setEditFormData} roles={roles} isSuper={isSuper} />
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell>
                        <EditableCell user={user} field="mobile" label="手機" editingUser={editingUser} editFormData={editFormData} setEditFormData={setEditFormData} roles={roles} isSuper={isSuper} />
                      </TableCell>
                      <TableCell>
                        <EditableCell user={user} field="community" label="社團" editingUser={editingUser} editFormData={editFormData} setEditFormData={setEditFormData} roles={roles} isSuper={isSuper} />
                      </TableCell>
                      <TableCell>
                        <EditableCell user={user} field="department" label="社別" editingUser={editingUser} editFormData={editFormData} setEditFormData={setEditFormData} roles={roles} isSuper={isSuper} />
                      </TableCell>
                      <TableCell>
                        <EditableCell user={user} field="position" label="社職" editingUser={editingUser} editFormData={editFormData} setEditFormData={setEditFormData} roles={roles} isSuper={isSuper} />
                      </TableCell>
                      <TableCell>
                        <EditableCell user={user} field="email" label="Email" editingUser={editingUser} editFormData={editFormData} setEditFormData={setEditFormData} roles={roles} isSuper={isSuper} />
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    <EditableCell user={user} field="roleId" type="select" label="角色" editingUser={editingUser} editFormData={editFormData} setEditFormData={setEditFormData} roles={roles} isSuper={isSuper} />
                    {!isEditing && (
                      <Chip 
                        label={user.role?.name || ''} 
                        size="small"
                        color={user.role?.name === '最高權限' ? 'error' : user.role?.name === '管理者' ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      {user.createdAt ? new Date(user.createdAt).toLocaleString() : ''}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {isEditing ? (
                        <>
                          <Tooltip title="保存">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditSave(user.id)}
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="取消">
                            <IconButton
                              size="small"
                              onClick={handleEditCancel}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="編輯">
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={editingUser !== null}
                              onClick={() => handleEdit(user)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {isSuper && (
                            <Tooltip title="刪除">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog({ open: true, userId: user.id })}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {user.lineId && !user.pictureUrl && (
                            <Tooltip title="刷新LINE資料">
                              <IconButton
                                size="small"
                                onClick={() => handleRefresh(user.id)}
                              >
                                <RefreshIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {user.community === 'IMC' && (
                            <Tooltip title="同步 OneIMC 社職">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleSyncOneimc(user)}
                              >
                                <SyncIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={isMobile ? 5 : 11} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchQuery ? '未找到符合條件的用戶' : '暫無用戶資料'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      <TablePagination
        component="div"
        count={data.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="每頁筆數："
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} / 共 ${count} 筆`}
      />

      {/* 刪除確認對話框 */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, userId: null })}
      >
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>確定要刪除此用戶嗎？此操作無法復原。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, userId: null })}>
            取消
          </Button>
          <Button 
            onClick={() => handleDelete(deleteDialog.userId)}
            color="error"
            variant="contained"
          >
            刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* OneIMC 同步確認對話框（姓名比對到，但社別跟目前記錄不同） */}
      <Dialog
        open={syncConfirmDialog.open}
        onClose={() => setSyncConfirmDialog({ open: false, userId: null, name: '', department: '', position: '', mobile: '', currentDepartment: '', currentMobile: '' })}
      >
        <DialogTitle>OneIMC 社別不一致，確認套用？</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            用姓名「{syncConfirmDialog.name}」在 OneIMC 查到資料，但社別跟目前記錄不同：
          </Typography>
          <Typography variant="body2" color="text.secondary">
            目前記錄：社別「{syncConfirmDialog.currentDepartment}」、手機「{syncConfirmDialog.currentMobile || '(未設定)'}」
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            OneIMC 實際：社別「{syncConfirmDialog.department}」、社職「{syncConfirmDialog.position}」、手機「{syncConfirmDialog.mobile || '(未提供)'}」
          </Typography>
          <Typography>確定是同一個人、要套用這筆資料嗎？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncConfirmDialog({ open: false, userId: null, name: '', department: '', position: '', mobile: '', currentDepartment: '', currentMobile: '' })}>
            取消
          </Button>
          <Button onClick={handleConfirmSyncOneimc} color="secondary" variant="contained">
            確定套用
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}